import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { baseOptions, makeCategoryRecord } from "@/lib/recipeOptions";
import type { CategoryCatalog, OptionCategory } from "@/types/recipe";

/**
 * Manages the per-category option catalog: user-added custom options, hidden
 * base options, and the dialog/input state used to edit them. Base options can
 * only be hidden (never deleted); custom options are removed outright.
 */
export const useCategoryOptions = (
  removeFromSelected: (category: OptionCategory, option: string) => void
): CategoryCatalog => {
  const [customOptions, setCustomOptions] = useState(() => makeCategoryRecord<string[]>(() => []));
  const [removedOptions, setRemovedOptions] = useState(() => makeCategoryRecord<string[]>(() => []));
  const [customInput, setCustomInput] = useState(() => makeCategoryRecord<string>(() => ""));
  const [customDialogOpen, setCustomDialogOpen] = useState(() =>
    makeCategoryRecord<boolean>(() => false)
  );

  const getVisibleOptions = (category: OptionCategory) =>
    [...baseOptions[category], ...customOptions[category]].filter(
      (option) => !removedOptions[category].includes(option)
    );

  const removeOption = (category: OptionCategory, option: string) => {
    if (baseOptions[category].includes(option)) {
      setRemovedOptions((prev) => ({
        ...prev,
        [category]: prev[category].includes(option)
          ? prev[category]
          : [...prev[category], option],
      }));
    } else {
      setCustomOptions((prev) => ({
        ...prev,
        [category]: prev[category].filter((item) => item !== option),
      }));
    }
    removeFromSelected(category, option);
  };

  const addCustomOption = (category: OptionCategory) => {
    const rawValue = customInput[category].trim();
    if (!rawValue) return;

    const values = rawValue
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    if (values.length === 0) return;

    const visibleSet = new Set(
      getVisibleOptions(category).map((option) => option.toLowerCase())
    );
    const nextCustom = [...customOptions[category]];
    let nextRemoved = [...removedOptions[category]];
    let addedCount = 0;
    let restoredCount = 0;

    values.forEach((value) => {
      const normalized = value.toLowerCase();
      const baseMatch = baseOptions[category].find(
        (option) => option.toLowerCase() === normalized
      );

      if (baseMatch && nextRemoved.includes(baseMatch)) {
        nextRemoved = nextRemoved.filter((option) => option !== baseMatch);
        visibleSet.add(normalized);
        restoredCount += 1;
        return;
      }

      if (visibleSet.has(normalized)) return;

      nextCustom.push(value);
      visibleSet.add(normalized);
      addedCount += 1;
    });

    if (addedCount + restoredCount === 0) {
      toast({
        title: "Already exists",
        description: "That option is already available in this category.",
      });
      return;
    }

    if (addedCount > 0) {
      setCustomOptions((prev) => ({ ...prev, [category]: nextCustom }));
    }
    if (restoredCount > 0) {
      setRemovedOptions((prev) => ({ ...prev, [category]: nextRemoved }));
    }

    setCustomInput((prev) => ({ ...prev, [category]: "" }));
    setCustomDialogOpen((prev) => ({ ...prev, [category]: false }));
  };

  return {
    getVisibleOptions,
    customInput,
    setCustomInput,
    customDialogOpen,
    setCustomDialogOpen,
    addCustomOption,
    removeOption,
  };
};
